<template lang="pug">
.camera-debug
  dl
    dt azimuth
    dd {{ azimuth }}
    dt polar
    dd {{ polar }}
    dt defaultZoom
    dd {{ zoom }}
    dt zoom slider
    dd {{ sliderZoom }}%
    dt distance
    dd {{ distance }}
  button(type="button" @click="copy") {{ copied ? 'Copied' : 'Copy camera' }}
</template>
<script lang="ts" setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import type { OrbitControls, Camera } from '@rogue/rackbuilder'
import type { Rogue3DEngine } from '../../composables/RogueEngineComposable'
import { STANDARD_FIT_OFFSET } from '../../composables/RackbuilderComposable'

const props = defineProps<{ engine: Rogue3DEngine, fitOffset?: number }>()

const controls = props.engine.engine?.controlsManager.orbitControls as OrbitControls
const camera = props.engine.engine?.camera as Camera

const azimuth = ref(0)
const polar = ref(0)
const zoom = ref(0)
const sliderZoom = ref(0)
const distance = ref(0)
const copied = ref(false)

const openingZoom = (props.fitOffset ?? STANDARD_FIT_OFFSET) / STANDARD_FIT_OFFSET
// Mounted right after the opening fit, so this is the distance that openingZoom produced; a later retarget makes the zoom readout stale until reload.
let openingDistance = 0

const snippet = computed(() => `defaultZoom: ${zoom.value}, angleDegrees: { azimuth: ${azimuth.value}, polar: ${polar.value} }`)

// Inverse of applyCameraAngle in RackbuilderComposable, so the numbers read here paste straight back into a product's camera config.
function read() {
  const offset = camera.position.clone().sub(controls.target)
  const length = offset.length()

  azimuth.value = Math.round(Math.atan2(offset.x, offset.z) * 180 / Math.PI)
  polar.value = Math.round(Math.acos(Math.min(1, Math.max(-1, offset.y / length))) * 180 / Math.PI)
  zoom.value = Math.round(openingZoom * length / openingDistance * 100) / 100
  // Same formula as the rogue-engine-controls slider, so it matches the position of the on-screen zoom bar.
  sliderZoom.value = Math.round(100 - ((length - controls.minDistance) / (controls.maxDistance - controls.minDistance)) * 100)
  distance.value = Math.round(length * 100) / 100
}

async function copy() {
  await navigator.clipboard.writeText(snippet.value)
  copied.value = true
  setTimeout(() => copied.value = false, 1500)
}

onMounted(() => {
  openingDistance = camera.position.distanceTo(controls.target)
  controls.addEventListener('change', read)
  read()
})

onUnmounted(() => controls.removeEventListener('change', read))
</script>
<style lang="scss" scoped>
.camera-debug {
  position: absolute;
  top: $space-s;
  left: $space-s;
  z-index: $z-index-sticky;
  padding: $space-xxs $space-xs;
  background: $color-black-light;
  color: $color-white;
  font-size: $size-xxs;
  border-radius: $radius-default;

  dl {
    display: grid;
    grid-template-columns: auto auto;
    gap: $space-xxxs $space-xs;
    margin: 0 0 $space-xxs;
  }

  dd {
    margin: 0;
    text-align: right;
  }

  button {
    width: 100%;
    font-size: $size-xxs;
  }
}
</style>
